import type { AdminSiteSettings, FuelHistoryRecord, FuelPrice, ManagedUser, MenuCategory, MenuItem, Permission, PublicSettings, ReleaseRecord, SessionUser, Special, SupportInfo } from '@heartland/shared';

import { customerAuditRows, customerReleaseChanges, customerText } from './customerPresentation';

export interface FuelSyncStatus {
  state: { mode: 'disabled' | 'manual' | 'automatic'; lastSuccessAt: string | null;
    fetchedAt: string | null; lastChangedAt: string | null; lastInvocationAt?: string | null;
    lastInvocationOutcome?: string; sourceUpdatedAt: Record<string, string>; failureCode: string | null; consecutiveFailures: number };
  config: { providerStatus: 'blocked' | 'ready' | 'not_configured'; provider: string; alertsEnabled: false };
  health: { status: string; overdue: boolean };
}

let csrfToken: string | null = null;
export const sessionExpiredEvent = 'heartland:session-expired';

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const isForm = init.body instanceof FormData;
  const response = await fetch(`/api${url}`, {
    credentials: 'include',
    cache: 'no-store',
    signal: AbortSignal.timeout(15000),
    headers: {
      ...(isForm ? {} : { 'content-type': 'application/json' }),
      ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
      ...init.headers
    },
    ...init
  });
  if (!response.ok) {
    if (response.status === 401 && url !== '/auth/login') {
      csrfToken = null;
      window.dispatchEvent(new Event(sessionExpiredEvent));
      throw new Error('Your session expired. Please sign in again.');
    }
    let message = `Request failed (${response.status}). Please retry.`;
    try { const data = await response.json(); if (typeof data.message === 'string') message = customerText(data.message); } catch { /* Keep a readable fallback. */ }
    throw new Error(message);
  }
  return response.json() as Promise<T>;
}

export const api = {
  async me() {
    const data = await request<{ user: SessionUser | null; csrfToken: string | null }>('/auth/me');
    csrfToken = data.csrfToken;
    return data.user;
  },
  async login(username: string, password: string) {
    const data = await request<{ user: SessionUser; csrfToken: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    });
    csrfToken = data.csrfToken;
    const confirmed = await this.me();
    if (!confirmed) throw new Error('Your sign-in session could not be established. Please retry.');
    return confirmed;
  },
  logout: () => request('/auth/logout', { method: 'POST', body: '{}' }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request('/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
  publicMenu: () => request<{ categories: MenuCategory[]; items: MenuItem[] }>('/public/menu'),
  publicSpecials: () => request<{ specials: Special[] }>('/public/specials'),
  publicFuelPrices: () => request<{ prices: FuelPrice[] }>('/public/fuel-prices'),
  publicSettings: () => request<PublicSettings>('/public/settings'),
  categories: () => request<{ categories: MenuCategory[] }>('/admin/categories'),
  saveCategory: (category: Partial<MenuCategory>) =>
    request(category.id ? `/admin/categories/${category.id}` : '/admin/categories', {
      method: category.id ? 'PUT' : 'POST',
      body: JSON.stringify(category)
    }),
  reorderCategories: (categoryIds: number[]) =>
    request<{ categories: MenuCategory[] }>('/admin/categories/reorder', { method: 'PUT', body: JSON.stringify({ categoryIds }) }),
  items: () => request<{ items: MenuItem[] }>('/admin/items'),
  saveItem: (item: Partial<MenuItem>) =>
    request(item.id ? `/admin/items/${item.id}` : '/admin/items', {
      method: item.id ? 'PUT' : 'POST',
      body: JSON.stringify(item)
    }),
  updatePrice: (id: number, price: string) =>
    request(`/admin/items/${id}/price`, { method: 'PATCH', body: JSON.stringify({ price }) }),
  deleteItem: (id: number) => request(`/admin/items/${id}`, { method: 'DELETE', body: '{}' }),
  reorderItems: (categoryId: number, itemIds: number[]) =>
    request<{ items: MenuItem[] }>('/admin/items/reorder', { method: 'PUT', body: JSON.stringify({ categoryId, itemIds }) }),
  uploadItemPhoto: (id: number, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return request<{ photoUrl: string; metadata: unknown }>(`/admin/items/${id}/photo`, { method: 'POST', body: form });
  },
  removeItemPhoto: (id: number) => request(`/admin/items/${id}/photo`, { method: 'DELETE', body: '{}' }),
  specials: () => request<{ specials: Special[] }>('/admin/specials'),
  saveSpecial: (special: Partial<Special>) =>
    request(special.id ? `/admin/specials/${special.id}` : '/admin/specials', {
      method: special.id ? 'PUT' : 'POST',
      body: JSON.stringify(special)
    }),
  users: () => request<{ users: Array<SessionUser & { disabledAt: string | null }> }>('/admin/users'),
  managedUsers: () => request<{ users: ManagedUser[] }>('/admin/users'),
  createUser: (user: unknown) => request('/admin/users', { method: 'POST', body: JSON.stringify(user) }),
  updateUser: (id: number, user: unknown) => request(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(user) }),
  updateUserPermissions: (id: number, permissions: Permission[]) => request(`/admin/users/${id}/permissions`, { method: 'PUT', body: JSON.stringify({ permissions }) }),
  resetUserPassword: (id: number, password: string, forcePasswordChange = true) =>
    request(`/admin/users/${id}/password`, { method: 'PATCH', body: JSON.stringify({ password, forcePasswordChange }) }),
  fuelSync: () => request<FuelSyncStatus>('/admin/fuel-sync'),
  setFuelSyncMode: (mode: 'disabled' | 'manual' | 'automatic') => request<FuelSyncStatus>('/admin/fuel-sync/mode', { method: 'PUT', body: JSON.stringify({ mode }) }),
  fuelPrices: () => request<{ prices: FuelPrice[] }>('/admin/fuel-prices'),
  fuelHistory: async (grade?: string) => {
    const data = await request<{ history: FuelHistoryRecord[] }>(`/admin/fuel-history${grade ? `?grade=${encodeURIComponent(grade)}` : ''}`);
    return { history: customerAuditRows(data.history) };
  },
  saveFuelPrices: (prices: Array<Pick<FuelPrice, 'grade' | 'price' | 'enabled'>>) =>
    request<{ prices: FuelPrice[] }>('/admin/fuel-prices', { method: 'PUT', body: JSON.stringify({ prices }) }),
  siteSettings: () => request<AdminSiteSettings>('/admin/site-settings'),
  saveSiteSettings: (settings: Pick<AdminSiteSettings, 'orderOnline'>) =>
    request('/admin/site-settings', { method: 'PUT', body: JSON.stringify(settings) }),
  uploadSpecialPhoto: (id: number, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return request<{ photoUrl: string; metadata: unknown }>(`/admin/specials/${id}/photo`, { method: 'POST', body: form });
  },
  removeSpecialPhoto: (id: number) => request(`/admin/specials/${id}/photo`, { method: 'DELETE', body: '{}' }),
  auditLogs: async () => { const data = await request<{ logs: Array<Record<string, unknown>> }>('/admin/audit-logs'); return { logs: customerAuditRows(data.logs) }; },
  releases: async () => { const data = await request<{ releases: ReleaseRecord[] }>('/admin/releases'); return { releases: data.releases.map(release => ({ ...release, summary: customerText(release.summary), changes: customerReleaseChanges(release.changes) })) }; },
  support: () => request<SupportInfo>('/admin/support')
};
